import React, { useEffect, useState } from "react";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import { debounce } from "lodash";
import { useSelector } from "../../../store";
import SiteType from "../../../types/siteType";
import SettingsModal from "../../../types/settingsModal";
import AddEditSiteTypeForm from "../../forms/AddEditSiteTypeForm";
import { Base } from "../../base";
import ColumnDef from "../../../types/columnDef";
import SearchContainer from "./SearchContainer";

interface Props {
  apiSiteTypeSearch: (search: SearchParams) => Promise<any>;
  phone: boolean;
  setDeleteModal: React.Dispatch<React.SetStateAction<SettingsModal>>;
  apiGetSiteType: (id: number) => void;
  resetSiteType: () => void;
  setSiteTypeFilter: (params: SearchParams) => void;
}

const SiteTypes: React.FC<Props> = (props) => {
  const {
    apiSiteTypeSearch,
    phone,
    setDeleteModal,
    apiGetSiteType,
    resetSiteType,
    setSiteTypeFilter,
  } = props;
  const { siteTypeSearchResults, siteTypeFilter, status } = useSelector(
    (store) => store.siteType
  );
  const [mobileRows, setMobileRows] = useState<any[]>([]);
  const [addEditModal, setAddEditModal] = useState(false);
  const [highlightId, setHighlightId] = useState(0);

  useEffect(() => {
    if (phone) {
      next();
    }
    return () => {
      setSiteTypeFilter(defaultParams);
    };
  }, [phone]);

  const updateSearch = debounce((query: string) => {
    setSiteTypeFilter({
      ...siteTypeFilter,
      query: query,
      pageNumber: phone ? 2 : 1,
      pageSize: 10,
    });
    apiSiteTypeSearch({
      ...siteTypeFilter,
      query: query,
      pageNumber: 1,
      pageSize: 10,
    }).then((result: any) => {
      setMobileRows(result);
    });
  }, 500);

  const columns: ColumnDef[] = [
    {
      field: "name",
      headerName: "Site Type",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((siteType: SiteType) => {
      let rowObject = {
        id: siteType.id,
        name: siteType.name,
        actions: (
          <Base.TableActions
            id={siteType.id}
            // TODO: Lock behind when we add roles
            handleDelete={() => {
              apiGetSiteType(siteType.id);
              setDeleteModal({ visible: true, modal: "siteType" });
              setHighlightId(siteType.id);
            }}
            handleEdit={() => {
              apiGetSiteType(siteType.id);
              setAddEditModal(true);
              setHighlightId(siteType.id);
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const changePage = (pageNumber?: number, pageSize?: number) => {
    apiSiteTypeSearch({
      ...siteTypeFilter,
      pageNumber,
      pageSize,
    }).then(() =>
      setSiteTypeFilter({
        ...siteTypeFilter,
        pageNumber,
        pageSize,
      })
    );
  };

  const next = () => {
    apiSiteTypeSearch(siteTypeFilter).then((result: any) => {
      setSiteTypeFilter({
        ...siteTypeFilter,
        pageNumber: siteTypeFilter?.pageNumber! + 1,
      });
      setMobileRows([...mobileRows, ...result]);
    });
  };

  const close = () => {
    apiSiteTypeSearch(defaultParams);
    setSiteTypeFilter(defaultParams);
    setAddEditModal(false);
    resetSiteType();
    setHighlightId(0);
  };

  const handleSaveUpdate = () => {
    apiSiteTypeSearch({ query: "", pageNumber: 1, pageSize: 10 }).then(
      (result: any) => {
        setMobileRows(result);
        setSiteTypeFilter({ ...siteTypeFilter, pageNumber: 2 });
      }
    );
  };

  const getOrderBy = (field: string) => {
    const fieldList = ["name"];
    return fieldList.find((value) => value === field) ?? undefined;
  };

  return (
    <>
      <SearchContainer
        updateSearch={updateSearch}
        searchValue={siteTypeFilter.query}
        modalOpen={setAddEditModal}
        reset={resetSiteType}
      >
        <Base.TableContainer
          columns={columns}
          rows={displayRows(siteTypeSearchResults?.results || [])}
          mobileRows={displayRows(mobileRows)}
          page={siteTypeFilter.pageNumber}
          pageSize={siteTypeFilter.pageSize}
          totalRows={siteTypeSearchResults?.totalResults}
          changePage={changePage}
          next={next}
          loading={status === "loading"}
          handleSort={(orderBy, orderDirection) => {
            const orderByField = orderBy ? getOrderBy(orderBy) : undefined;
            apiSiteTypeSearch({
              ...siteTypeFilter,
              orderBy: orderByField,
              orderDirection,
            });
          }}
          highlightId={highlightId}
        />
      </SearchContainer>
      <Base.Modal open={addEditModal} onClose={close}>
        <AddEditSiteTypeForm close={close} onSave={handleSaveUpdate} />
      </Base.Modal>
    </>
  );
};

export default SiteTypes;
